import React, { Component } from 'react';
import { string, bool, func, object } from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl, intlShape, FormattedDate } from '../../util/reactIntl';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import { Form } from '../../components';
import { propTypes } from '../../util/types';
import { formatMoney } from '../../util/currency';

import css from './CustomOfferAcceptedForm.css';
import { types as sdkTypes } from '../../util/sdkLoader';
import { previewTotals, legacyPreviewResolution, rateLabelFor, FEE_MODEL } from '../../util/vat';

const { Money } = sdkTypes;

const BLUR_TIMEOUT_MS = 100;

class CustomOfferAcceptedFormComponent extends Component {
  constructor(props) {
    super(props);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.blurTimeoutId = null;
  }

  handleFocus() {
    this.props.onFocus();
    window.clearTimeout(this.blurTimeoutId);
  }

  handleBlur() {
    // We only trigger a blur if another focus event doesn't come
    // within a timeout. This enables keeping the focus synced when
    // focus is switched between the message area and the submit
    // button.
    this.blurTimeoutId = window.setTimeout(() => {
      this.props.onBlur();
    }, BLUR_TIMEOUT_MS);
  }

  render() {
    return (
      <FinalForm
        {...this.props}
        render={formRenderProps => {
          const {
            rootClassName,
            className,
            isProvider,
            customOfferAmount,
            customOfferPlusVATAmount,
            customOfferVATAmount,
            customOfferTitle,
            customOfferDescription,
            customOfferStartDate,
            customOfferDeliveryDate,
            handleSubmit,
            customOfferError,
            form,
            intl,
            isProOrInvoiceCustomer,
            showCustomOfferFormAcceptedProvider,
            vatResolution,
            totals,
          } = formRenderProps;

          const classes = classNames(rootClassName || css.root, className);
          const resolution = vatResolution || legacyPreviewResolution();
          // The panel hands in the transaction's totals (its line items once
          // accepted); without them this previews from the offer amount.
          const t =
            totals ||
            previewTotals({
              path: resolution.path,
              resolution,
              customerPrice: customOfferAmount.amount,
            });
          const work = new Money(t.net, 'EUR');
          const serviceFee = new Money(t.fee, 'EUR');
          const showServiceFee = t.feeModel !== FEE_MODEL.PROVIDER;
          const freelancerWillReceive = new Money(t.providerNet, 'EUR');
          const freedomlyCommission = new Money(t.fee + t.feeVat, 'EUR');
          const supplies = resolution.supplies || {};
          const customerSupply = supplies.core || supplies.customerSale;
          const offerRateLabel = rateLabelFor(customerSupply, intl.locale);
          const showOfferVat = !!customerSupply && customerSupply.rate > 0;
          const modalProProviderMessage = (
            <FormattedMessage id="TransactionPanel.acceptProProviderModalMessage" />
          );

          return (
            <Form className={classes} onSubmit={values => handleSubmit(values, form)}>
              <div className={css.customOfferDescription}>
                <FormattedMessage id="CustomOfferForm.customOfferDescription" />
              </div>
              {customOfferTitle ? (
                <div className={css.customOfferTitle}>{customOfferTitle}</div>
              ) : null}

              {/* Absent on offers made before the start date field existed. */}
              {customOfferStartDate ? (
                <div className={css.deliveryContainer}>
                  <FormattedMessage id="CustomOfferForm.startDate" />
                  <FormattedDate value={customOfferStartDate} />
                </div>
              ) : null}
              <div className={css.deliveryContainer}>
                <FormattedMessage id="CustomOfferForm.deliveryDate" />
                <FormattedDate value={customOfferDeliveryDate} />
              </div>
              <div className={css.submitContainer}>
                <div className={css.customOfferPrice}>
                  <FormattedMessage
                    id="CustomOfferForm.priceLabel"
                    values={{ rateLabel: offerRateLabel }}
                  />
                  &nbsp;
                  {customOfferAmount ? formatMoney(intl, customOfferPlusVATAmount) : null}
                </div>
                {showOfferVat ? (
                  <div className={css.customOfferVAT}>
                    <FormattedMessage
                      id="CustomOfferForm.bidAmountVAT"
                      values={{ rateLabel: offerRateLabel }}
                    />
                    &nbsp;
                    {customOfferAmount ? formatMoney(intl, customOfferVATAmount) : null}
                  </div>
                ) : null}
                <div className={css.customOfferService}>
                  <FormattedMessage id="CustomOfferForm.serviceLabel" />
                  &nbsp;
                  <div className={css.customOfferServiceAmount}>
                    {customOfferAmount ? formatMoney(intl, work) : null}
                  </div>
                </div>
                {showServiceFee ? (
                  <div className={css.customOfferService}>
                    <FormattedMessage
                      id="CustomOfferForm.serviceFee"
                      values={{ percentage: t.feePercentage }}
                    />
                    &nbsp;
                    <div className={css.customOfferServiceAmount}>
                      {customOfferAmount ? formatMoney(intl, serviceFee) : null}
                    </div>
                  </div>
                ) : null}
                {isProvider ? (
                  <>
                    <div className={css.customOfferService}>
                      <FormattedMessage id="CustomOfferForm.freedomlyCommission" />
                      &nbsp;
                      <div className={css.customOfferServiceAmount}>
                        {freedomlyCommission ? formatMoney(intl, freedomlyCommission) : null}
                      </div>
                    </div>

                    <div className={css.customOfferService}>
                      <FormattedMessage id="CustomOfferForm.freelancerWillReceiveLabel2" />
                      &nbsp;
                      <div className={css.customOfferServiceAmount}>
                        {freelancerWillReceive ? formatMoney(intl, freelancerWillReceive) : null}
                      </div>
                    </div>
                  </>
                ) : null}
                <div className={css.customOfferDescriptionContent}>{customOfferDescription}</div>

                <div className={css.errorContainer}>
                  {customOfferError ? (
                    <p className={css.error}>
                      <FormattedMessage id="CustomOfferForm.operationFailed" />
                    </p>
                  ) : null}
                </div>
                {/* {isProOrInvoiceCustomer && !isProvider ? (
                  <div className={css.customOfferDescriptionPro}>{modalProMessage}</div>
                ) : null} */}
                {isProOrInvoiceCustomer && isProvider && showCustomOfferFormAcceptedProvider ? (
                  <div className={css.customOfferDescriptionPro}>{modalProProviderMessage}</div>
                ) : null}
              </div>
            </Form>
          );
        }}
      />
    );
  }
}

CustomOfferAcceptedFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  inProgress: false,
  messagePlaceholder: null,
  onFocus: () => null,
  onBlur: () => null,
  sendMessageError: null,
  vatResolution: null,
  totals: null,
};

CustomOfferAcceptedFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  inProgress: bool,
  // Offers made before the title field existed have none.
  customOfferTitle: string,
  // Offers made before the start date field existed have none.
  customOfferStartDate: string,

  messagePlaceholder: string,
  onSubmit: func.isRequired,
  onFocus: func,
  onBlur: func,
  sendMessageError: propTypes.error,
  // The panel's VAT resolution (snapshot, ok preview or legacy).
  vatResolution: object,
  // The transaction's totals from TransactionPanel (previewTotals shape).
  totals: object,

  // from injectIntl
  intl: intlShape.isRequired,
};

const CustomOfferAcceptedForm = compose(injectIntl)(CustomOfferAcceptedFormComponent);

CustomOfferAcceptedForm.displayName = 'CustomOfferAcceptedForm';

export default CustomOfferAcceptedForm;
